// LAP-752 — Monthly emission line chart with user / objective / FR average
// overlay. Replaces the "feels too basic" charts called out in the ticket by
// surfacing the user's defined objective and the national average directly
// on the same canvas as the user's curve.
//
// Implementation note: we render raw SVG (no external chart lib) for two
// reasons:
//   1. The rest of the dashboard uses MUI + custom SVG already.
//   2. Recharts pulls in a 100+ KB chunk we don't need just for one chart.
import { Box, Card, Button } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import HomeWorkIcon from '@mui/icons-material/HomeWork'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import WTypography from '../../../../Components/whiteTypography'
import { wizardAPI } from '../../../../utils/wizardAPI'

const PERIODS = ['6m', '12m', '5y']

function EcoTrendChart() {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  const [period, setPeriod] = useState('12m')
  const [series, setSeries] = useState([])
  // LAP-752 — backend flag: true if at least one month in the window has a
  // non-zero user_total_t. Drives the "guided empty state" below.
  const [hasUserData, setHasUserData] = useState(false)
  const [countryName, setCountryName] = useState(null)
  const [countryDataOk, setCountryDataOk] = useState(true)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    wizardAPI
      .getEmissionTrend(period)
      .then((d) => {
        if (cancelled) return
        setSeries(d?.series || [])
        setHasUserData(!!d?.has_user_data)
        setCountryName(d?.country_name || null)
        setCountryDataOk(d?.country_data_available !== false)
      })
      .catch(() => {
        if (!cancelled) {
          setSeries([])
          setHasUserData(false)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [period])

  // Build SVG paths from data. We normalise to viewBox 0..480 × 0..230 so
  // CSS scaling works on any container width.
  const W = 480
  const H = 230
  const padLeft = 0
  const padRight = 30
  const padBottom = 30
  const padTop = 20

  const maxY = Math.max(
    1,
    ...series.map((p) => Math.max(p.user_total_t || 0, p.fr_average_t || 0, p.objective_t || 0))
  )
  const xStep = series.length > 1 ? (W - padLeft - padRight) / (series.length - 1) : 0
  const yScale = (v) => H - padBottom - ((v || 0) / maxY) * (H - padTop - padBottom)
  const xScale = (i) => padLeft + i * xStep

  const userPath = series
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(i)},${yScale(p.user_total_t)}`)
    .join(' ')
  const areaPath = series.length
    ? `${userPath} L${xScale(series.length - 1)},${H - padBottom} L0,${H - padBottom} Z`
    : ''

  // FR average is constant across the window — flat dashed line.
  const frY = series.length ? yScale(series[0].fr_average_t) : H / 2
  // Objective is also constant per window (yearly target / 12).
  const objVal = series.find((p) => p.objective_t != null)?.objective_t
  const objY = objVal != null ? yScale(objVal) : null

  return (
    <Card className="dashboard_card__root1">
      <Box className="dashboard_card__header1" style={{ marginBottom: 8 }}>
        <WTypography component="span" variant="h6">
          {t('dashboard.emissionTrendTitle', 'Évolution sur 12 mois')}
        </WTypography>
        <Box display="flex" gap={1} className="no-drag">
          {PERIODS.map((p) => (
            <span
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                fontSize: 12,
                padding: '4px 10px',
                borderRadius: 7,
                cursor: 'pointer',
                color: p === period ? '#b6f0d4' : 'rgba(255,255,255,0.5)',
                background: p === period ? 'rgba(47,210,138,0.16)' : 'transparent',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {p.toUpperCase()}
            </span>
          ))}
        </Box>
      </Box>

      {/* Legend */}
      <Box display="flex" gap={2} mb={1} style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
        <span>
          <span style={legendSwatch('#2fd28a')} />{' '}
          {t('dashboard.legendYou', 'Vous')}
        </span>
        <span>
          <span style={legendSwatch('#7aa6ff')} />{' '}
          {t('dashboard.legendObjective', 'Objectif')}
        </span>
        <span>
          <span style={legendSwatch('#9fb0d2', 0.6)} />{' '}
          {t('dashboard.legendFrAvg', 'Moyenne FR')}
        </span>
      </Box>

      {/* LAP-752 — surface a notice when we fell back to the FR baseline
          because the user's country has no NationalEmission row. The
          support team has been notified automatically (24h cooldown). */}
      {!loading && !countryDataOk && countryName && (
        <Box
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            fontSize: 12,
            color: '#ffcdb8',
            bgcolor: 'rgba(217,119,87,0.12)',
            border: '1px solid rgba(217,119,87,0.3)',
          }}
        >
          {t(
            'dashboard.countryFallbackNotice',
            {
              country: countryName,
              defaultValue:
                'Moyenne provisoire (France) pour {{country}} — données nationales en cours d\'ajout.',
            }
          )}
        </Box>
      )}

      {loading ? (
        <Box height={230} display="flex" alignItems="center" justifyContent="center">
          <WTypography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            {t('dashboard.loading', 'Chargement…')}
          </WTypography>
        </Box>
      ) : !hasUserData ? (
        // LAP-752 — Guided empty state. Replaces the dull "No data" with
        // an actionable explanation + 4 quick-jump CTAs to the data-entry
        // pages (Logement, Transport, Alimentation, Shopping). Same parcours
        // as the per-category empty cards on the dashboard for consistency.
        <Box
          height={260}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          px={2}
        >
          <WTypography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
            {t('dashboard.trendEmptyTitle', 'Démarrez le suivi de votre empreinte')}
          </WTypography>
          <WTypography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 400, mb: 2, fontSize: 13 }}
          >
            {t(
              'dashboard.trendEmptyDesc',
              'Renseignez au moins une catégorie pour voir votre courbe d\'évolution. Plus vous remplissez, plus l\'IA peut personnaliser ses recommandations.'
            )}
          </WTypography>
          <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" className="no-drag">
            <Button
              size="small"
              startIcon={<HomeWorkIcon />}
              onClick={() => navigate('/domicile/monimmobilier')}
              sx={emptyCtaStyle}
            >
              {t('dashboard.catLogement', 'Logement')}
            </Button>
            <Button
              size="small"
              startIcon={<DirectionsCarIcon />}
              onClick={() => navigate('/domicile/mesvehicules')}
              sx={emptyCtaStyle}
            >
              {t('dashboard.catTransport', 'Transport')}
            </Button>
            <Button
              size="small"
              startIcon={<RestaurantIcon />}
              onClick={() => navigate('/Dashboard?questionnaire=alimentation')}
              sx={emptyCtaStyle}
            >
              {t('dashboard.catAlimentation', 'Alimentation')}
            </Button>
            <Button
              size="small"
              startIcon={<ShoppingBagIcon />}
              onClick={() => navigate('/shopping')}
              sx={emptyCtaStyle}
            >
              {t('dashboard.catShopping', 'Shopping')}
            </Button>
          </Box>
        </Box>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 230, display: 'block' }}>
          <defs>
            <linearGradient id="ecoTrendGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#2fd28a" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#2fd28a" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Gridlines */}
          {[0.25, 0.5, 0.75].map((r) => (
            <line
              key={r}
              x1={0}
              y1={padTop + r * (H - padTop - padBottom)}
              x2={W}
              y2={padTop + r * (H - padTop - padBottom)}
              stroke="rgba(255,255,255,0.06)"
            />
          ))}

          {/* FR average dashed */}
          <line
            x1={0}
            y1={frY}
            x2={W}
            y2={frY}
            stroke="#9fb0d2"
            strokeWidth="1.5"
            strokeDasharray="5 5"
            opacity="0.6"
          />
          {/* Objective dashed */}
          {objY != null && (
            <line
              x1={0}
              y1={objY}
              x2={W}
              y2={objY}
              stroke="#7aa6ff"
              strokeWidth="1.5"
              strokeDasharray="3 3"
              opacity="0.8"
            />
          )}

          {/* User area */}
          {areaPath && <path d={areaPath} fill="url(#ecoTrendGrad)" />}
          {/* User line */}
          <path
            d={userPath}
            fill="none"
            stroke="#2fd28a"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Last point */}
          {series.length > 0 && (
            <circle
              cx={xScale(series.length - 1)}
              cy={yScale(series[series.length - 1].user_total_t)}
              r="4"
              fill="#2fd28a"
              stroke="#fff"
              strokeWidth="1.5"
            />
          )}

          {/* X axis labels (first / middle / last month) */}
          <g fill="rgba(255,255,255,0.4)" fontSize="10">
            <text x={0} y={H - 8}>
              {series[0]?.month?.slice(2, 7)}
            </text>
            <text x={W / 2} y={H - 8} textAnchor="middle">
              {series[Math.floor(series.length / 2)]?.month?.slice(2, 7)}
            </text>
            <text x={W - padRight} y={H - 8} textAnchor="end">
              {series[series.length - 1]?.month?.slice(2, 7)}
            </text>
          </g>
        </svg>
      )}
    </Card>
  )
}

function legendSwatch(color, opacity = 1) {
  return {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: 3,
    background: color,
    opacity,
    marginRight: 4,
    verticalAlign: 'middle',
  }
}

const emptyCtaStyle = {
  textTransform: 'none',
  color: '#b6f0d4',
  background: 'rgba(47,210,138,0.16)',
  border: '1px solid rgba(47,210,138,0.4)',
  fontSize: 12,
  fontWeight: 600,
  px: 1.5,
  '&:hover': { background: 'rgba(47,210,138,0.28)', borderColor: '#2fd28a' },
}

export default EcoTrendChart
